import { Box, Button, CircularProgress, makeStyles, Typography } from '@material-ui/core';
import AttachmentIcon from '@material-ui/icons/Attachment';
import BrokenImageIcon from '@material-ui/icons/BrokenImage';
import React, { ReactElement } from 'react';

import TextIcon from 'common/components/icons/text';
import DownloadForOfflineIcon from '@mui/icons-material/DownloadForOffline';
import { getMediaType, getMediaTypeFromMimeType, isHtmlSupportedAudio, isHtmlSupportedVideo, isHtmlSupportedAudioByMimeType, isHtmlSupportedVideoByMimeType, MediaType } from 'common/utils/media-type';

import ImageModalContext from '../image-modal-context';
import AudioIcon from 'common/components/icons/audio';
import Video from 'common/components/icons/video';
import { Media } from 'common/models/media';
import PDFPreviewModal from './pdf-preview-modal';
import DocumentPreviewModal from './document-preview-modal';
import DocumentBubble from './document-bubble';
import { supportsOnlinePreview, getDocumentIcon, extractFileName } from './document-utils';
const useMessageMediaStyle = makeStyles((theme) => ({
  media: {
    marginBottom: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  mediaWrapper: {
    marginBottom: theme.spacing(1),
    marginRight: theme.spacing(1),
    marginTop: 0,
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
  },
  thumb: {
    cursor: 'pointer',
    display: 'inline-flex',
    borderRadius: theme.spacing(0.5),
    border: '1px solid #eaeaea',
    width: 'auto',
    height: 100,
    padding: theme.spacing(0.5),
    boxSizing: 'border-box',
    transition: 'all 0.2s ease',
    '&:hover': {
      boxShadow: theme.shadows[2],
      borderColor: theme.palette.primary.main,
    },
  },
  thumbInner: {
    display: 'flex',
    minWidth: 0,
    overflow: 'hidden',
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  img: {
    display: 'block',
    borderRadius: theme.spacing(0.5),
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    background: '#f9f9f9'
  },
  imageLoadingOverlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(145deg, #f5f7fa 0%, #edf1f5 100%)',
    zIndex: 1,
  },
  imageErrorState: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(0.5),
    color: theme.palette.grey[600],
    background: 'linear-gradient(145deg, #f8f8f8 0%, #eeeeee 100%)',
  },
  imageErrorText: {
    fontSize: '0.65rem',
    fontWeight: 500,
  },
}));

function VideoMedia({ src, mimeType }: { src: string; mimeType?: string }): React.ReactElement {
  const [isAudioOnly, setIsAudioOnly] = React.useState(false);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const classes: any = useMessageMediaStyle();

  React.useEffect(() => {
    setIsAudioOnly(false); // reset on src change
  }, [src]);

  function handleLoadedMetadata() {
    const video = videoRef.current;
    if (video) {
      // If there are no video tracks, treat as audio only
      // Some browsers expose videoTracks, but fallback to videoWidth/Height
      const hasVideoTrack = (video.videoWidth > 0 && video.videoHeight > 0);
      if (!hasVideoTrack) {
        setIsAudioOnly(true);
      }
    }
  }

  const isSupported = mimeType ? isHtmlSupportedVideoByMimeType(mimeType) : isHtmlSupportedVideo(src);
  
  if (isSupported) {
    if (isAudioOnly) {
      // Render as audio player if no video track
      return (
        <Box className={classes.mediaWrapper}>
          <audio controls>
            <source src={src} />
            Your browser does not support the <code>audio</code> element.
          </audio>
        </Box>
      );
    }
    return (
      <Box className={classes.mediaWrapper}>
        <video
          controls
          width="250"
          ref={videoRef}
          onLoadedMetadata={handleLoadedMetadata}
          style={{ maxHeight: 150 }}
        >
          <source src={src} />
          Sorry, your browser doesn't support embedded videos.
        </video>
      </Box>
    );
  }
  return <DownloadMedia src={src} type="Video" icon={<Video />} mimeType={mimeType} />;
}

function AudioMedia({ src, mimeType }: { src: string; mimeType?: string }): React.ReactElement {
  const classes: any = useMessageMediaStyle();
  const isSupported = mimeType ? isHtmlSupportedAudioByMimeType(mimeType) : isHtmlSupportedAudio(src);
  
  if (isSupported) {
    return (
      <Box className={classes.mediaWrapper}>
        <audio controls>
          <source src={src} />
          Your browser does not support the
          <code>audio</code> element.
        </audio>
      </Box>
    );
  }
  return <DownloadMedia src={src} type="Audio" icon={<AudioIcon />} mimeType={mimeType} />;
}

function ImageMedia({ src }: { src: string }): React.ReactElement {
  const classes: any = useMessageMediaStyle();
  const { openImageModal } = React.useContext(ImageModalContext);
  const isBlobImage = src.startsWith('blob:');
  const [isLoading, setIsLoading] = React.useState(isBlobImage);
  const [hasError, setHasError] = React.useState(false);

  React.useEffect(() => {
    setHasError(false);
    setIsLoading(src.startsWith('blob:'));
  }, [src]);

  function handleOnClick(event: any): void {
    if (hasError || isLoading) {
      return;
    }
    openImageModal(src);
  }

  return (
    <Box className={classes.mediaWrapper}>
      <Box className={classes.thumb} onClick={handleOnClick}>
        <div className={classes.thumbInner}>
          {isLoading && (
            <Box className={classes.imageLoadingOverlay}>
              <CircularProgress size={20} thickness={5} />
            </Box>
          )}
          {hasError ? (
            <Box className={classes.imageErrorState}>
              <BrokenImageIcon fontSize="small" />
              <Typography className={classes.imageErrorText}>Image unavailable</Typography>
            </Box>
          ) : (
            <img
              src={src}
              className={classes.img}
              alt={src}
              onLoad={() => setIsLoading(false)}
              onError={() => {
                setIsLoading(false);
                setHasError(true);
              }}
            />
          )}
        </div>
      </Box>
    </Box>
  );
}
function GIFMedia({ src }: { src: string }): React.ReactElement {
  const classes: any = useMessageMediaStyle();
  return (
    <Box className={classes.mediaWrapper}>
      <Box className={classes.thumb}>
        <div className={classes.thumbInner}>
          <img src={src} className={classes.img} alt="GIF" />
        </div>
      </Box>
    </Box>
  );
}
function StickerMedia({ src }: { src: string }): React.ReactElement {
  const classes: any = useMessageMediaStyle();
  return (
    <Box className={classes.mediaWrapper}>
      <Box className={classes.thumb}>
        <div className={classes.thumbInner}>
          <img src={src} className={classes.img} alt="Sticker" />
        </div>
      </Box>
    </Box>
  );
}

function PDFMedia({ src, mimeType }: { src: string; mimeType?: string }): React.ReactElement {
  const [openPreview, setOpenPreview] = React.useState(false);
  const fileName = extractFileName(src);

  const handleOpenPreview = () => {
    setOpenPreview(true);
  };

  return (
    <>
      <DocumentBubble
        fileName={fileName}
        mimeType={mimeType || 'application/pdf'}
        onClick={handleOpenPreview}
      />
      <PDFPreviewModal
        open={openPreview}
        onClose={() => setOpenPreview(false)}
        pdfUrl={src}
        fileName={fileName}
      />
    </>
  );
}

function TextMedia({ src, mimeType }: { src: string; mimeType?: string }): React.ReactElement {
  const [openPreview, setOpenPreview] = React.useState(false);
  const fileName = extractFileName(src);
  const resolvedMimeType = mimeType || (fileName.toLowerCase().endsWith('.csv') ? 'text/csv' : 'text/plain');
  const canPreview = supportsOnlinePreview(resolvedMimeType);

  return (
    <>
      <DocumentBubble
        fileName={fileName}
        mimeType={resolvedMimeType}
        onClick={() => {
          if (canPreview) {
            setOpenPreview(true);
          } else {
            window.open(src, '_blank');
          }
        }}
      />
      {canPreview && (
        <DocumentPreviewModal
          open={openPreview}
          onClose={() => setOpenPreview(false)}
          documentUrl={src}
          mimeType={resolvedMimeType}
          fileName={fileName}
        />
      )}
    </>
  );
}

function DownloadMedia({
  src,
  type = '',
  icon = <AttachmentIcon />,
  mimeType = '',
}: {
  src: string;
  type: string;
  icon: React.ReactElement;
  mimeType?: string;
}): React.ReactElement {
  const [openPreview, setOpenPreview] = React.useState(false);
  const canPreview = supportsOnlinePreview(mimeType);
  const fileName = extractFileName(src);

  function handleOnClick(): void {
    if (canPreview) {
      setOpenPreview(true);
    } else {
      window.open(src, '_blank');
    }
  }

  return (
    <>
      <DocumentBubble
        fileName={fileName}
        mimeType={mimeType}
        onClick={handleOnClick}
      />
      {canPreview && (
        <DocumentPreviewModal
          open={openPreview}
          onClose={() => setOpenPreview(false)}
          documentUrl={src}
          mimeType={mimeType}
          fileName={fileName}
        />
      )}
    </>
  );
}

export function MessageMedia({ mediaSrc }: { mediaSrc: string | Media }): ReactElement {
  let mediaType = '';
  let url = '';
  let mimeType = '';

  if (typeof mediaSrc == 'string') {
    mediaType = getMediaType(mediaSrc);
    url = mediaSrc;
  } else {
    const mediaObject = mediaSrc as Media & {
      mediaUrl?: string;
      contentType?: string;
      fileType?: string;
    };

    // Handle attachment payloads that may use alternate keys
    url = mediaObject.url || mediaObject.mediaUrl || '';
    mimeType = mediaObject.mimeType || mediaObject.contentType || mediaObject.fileType || '';

    if (mimeType) {
      mediaType = getMediaTypeFromMimeType(mimeType);
    } else {
      mediaType = getMediaType(url);
    }
  }

  if (!url) {
    return <></>;
  }

  const mediaComponent: any = {
    [MediaType.Image]: ImageMedia,
    [MediaType.Video]: VideoMedia,
    [MediaType.Audio]: AudioMedia,
    [MediaType.Text]: TextMedia,
    [MediaType.PDF]: PDFMedia,
    [MediaType.GIF]: GIFMedia,
    [MediaType.Sticker]: StickerMedia,
    [MediaType.Other]: DownloadMedia,
  };

  const MediaComponent = mediaComponent[mediaType];

  if (mediaType === MediaType.Video) {
    return MediaComponent && <MediaComponent src={url} mimeType={mimeType} />;
  }
  if (mediaType === MediaType.Audio) {
    return MediaComponent && <MediaComponent src={url} mimeType={mimeType} />;
  }
  if (mediaType === MediaType.Text) {
    return MediaComponent && <MediaComponent src={url} mimeType={mimeType} />;
  }
  if (mediaType === MediaType.PDF) {
    return MediaComponent && <MediaComponent src={url} mimeType={mimeType} />;
  }
  if (mediaType === MediaType.Other) {
    return MediaComponent && <MediaComponent src={url} type="" icon={<AttachmentIcon />} mimeType={mimeType} />;
  }

  return MediaComponent && <MediaComponent src={url} />;
}

export default MessageMedia;


